import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../lib/utils.js';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Move focus to the first control in `content` that is NOT the dismiss X.
 *
 * ⚠️ THIS EXISTS BECAUSE THE X IS NOW FIRST IN DOM. Radix's `FocusScope` focuses
 * `focusFirst(removeLinks(getTabbableCandidates(container)))` on mount, i.e. the
 * first tabbable child in DOM ORDER — so moving the X to the front of the tree
 * for tab-order reasons would otherwise have made "dismiss without saving" the
 * element focused ON OPEN in every dialog in the product. Tab order and initial
 * focus are two different decisions and this is where they are separated.
 *
 * It reproduces radix's own choices rather than approximating them: `tabIndex
 * >= 0` (so a scroll container or focus-by-script heading cannot take it),
 * links skipped as `removeLinks` skips them while staying in the TAB order,
 * each candidate tried until one actually TAKES focus — which is what makes
 * `disabled`, `hidden` and `type="hidden"` fall out with no filter of their own
 * — and a text input's contents selected. Returns false when the X is the only
 * thing there is, leaving radix's default alone. Every one of those is pinned
 * in `__tests__/dialog.closeOrder.dom.test.tsx`.
 *
 * ⚠️ THOSE RADIX INTERNALS ARE A TRANSITIVE DEPENDENCY NOTHING HERE PINS.
 * `focusFirst` / `removeLinks` / `getTabbableCandidates` belong to
 * `@radix-ui/react-focus-scope`, which arrives under `@radix-ui/react-dialog`
 * (declared `^1.1.14`, resolving at the time of writing to 1.1.15 →
 * focus-scope 1.1.7). A `pnpm update` can move either without a line here
 * changing. The OUTCOME is what the suite asserts, so a radix change surfaces
 * as a failure there rather than as silent drift from this comment.
 *
 * ⚠️ ONE DELIBERATE FIDELITY GAP. radix stops as soon as focus MOVED AT ALL
 * (`activeElement !== previouslyFocusedElement`); this asks the narrower
 * `!== element` — did THIS candidate take it. They differ only for a control
 * that redirects focus to a DESCENDANT when focused. Nothing in the product
 * does that today, and the narrow comparison is exactly what makes the
 * disabled/hidden cases fall out for free, so it stays. If a redirecting
 * control ever lands in a dialog the symptom is initial focus one field too far
 * down — not a trap — and this is the paragraph to come back to.
 */
function focusFirstControlAfterClose(
  content: HTMLElement,
  close: HTMLElement | null,
): boolean {
  const walker = content.ownerDocument.createTreeWalker(content, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) =>
      (node as HTMLElement).tabIndex >= 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
  });
  while (walker.nextNode()) {
    const element = walker.currentNode as HTMLElement;
    if (element.tagName === 'A') continue;
    if (close !== null && close.contains(element)) continue;
    element.focus({ preventScroll: true });
    if (content.ownerDocument.activeElement !== element) continue;
    if (element instanceof HTMLInputElement) element.select();
    return true;
  }
  return false;
}

/**
 * Bounded height, and it scrolls.
 *
 * Without `max-h` the content grew to whatever its children needed, so a tall
 * dialog ran off the viewport with its FOOTER — Save / Cancel — below the fold
 * and no way to reach them: the overlay swallows page scroll, and the content
 * had no scroller of its own. `100dvh` rather than `vh` so a mobile browser's
 * collapsing toolbar does not hide the last row.
 *
 * `flex flex-col` rather than `grid` so a caller can hand ONE child the
 * remaining space: wrap the long part in <DialogBody> and the title and the
 * buttons stay put while only that part scrolls. Without a <DialogBody> the
 * whole box scrolls exactly as it did — the header and footer ride away with
 * the content, which is fine for a short dialog and was the only behaviour
 * before. Column flex stacks and stretches its children the same way the grid
 * did, so no existing dialog changes shape.
 *
 * Callers can still override via `className`; a dialog that manages its own
 * internal scrolling is unaffected, since the cap only binds when the content
 * would otherwise exceed the viewport.
 *
 * ⚠️ THE DISMISS X IS FIRST IN DOM, AND IS STILL DRAWN TOP-RIGHT. Rendered
 * after `{children}` it put Tab-from-the-primary-action onto dismiss in EVERY
 * dialog in the product — one keystroke from discarding an unsaved form (B15 /
 * §3.11 item 7). It is `absolute right-4 top-4`, so tree position decides tab
 * order and nothing visual. It is NOT inert for initial focus, which is what
 * `focusFirstControlAfterClose` above exists to hold.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onOpenAutoFocus, ...props }, ref) => {
  const closeRef = React.useRef<HTMLButtonElement>(null);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 flex max-h-[calc(100dvh-2rem)] flex-col w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className,
        )}
        {...props}
        onOpenAutoFocus={(event) => {
          // A caller that places focus itself still wins: it runs first, and
          // its `preventDefault()` (BackEdgeConfirm.tsx) stops both radix and
          // this handler.
          onOpenAutoFocus?.(event);
          if (event.defaultPrevented) return;
          const content = event.currentTarget as HTMLElement | null;
          if (content === null) return;
          if (focusFirstControlAfterClose(content, closeRef.current)) {
            event.preventDefault();
          }
        }}
      >
        <DialogPrimitive.Close
          ref={closeRef}
          className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring focus:shadow-[var(--shadow-focus)] disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * The one child that absorbs the leftover height and scrolls.
 *
 * `min-h-0` is load-bearing: a flex item's default `min-height: auto` refuses
 * to shrink below its content, so without it the body pushes the footer past
 * the cap instead of scrolling and nothing here works. Optional — a dialog
 * with no long section needs no body at all.
 */
const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto', className)} {...props} />
);
DialogBody.displayName = 'DialogBody';

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
